import type { QueryClient } from '@tanstack/react-query'
import { Link, Outlet, createRootRouteWithContext, useRouterState } from '@tanstack/react-router'
import { Toaster } from 'sonner'

import { AppShell } from '@/components/app-shell'
import { CommandPalette } from '@/components/command-palette'
import { Button } from '@/components/ui/button'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthGate } from '@/features/auth/AuthGate'
import { useDensityAttribute } from '@/features/preferences/preferences-store'
import { GlobalScanListener } from '@/features/scan/ScanHandler'
import { useThemeStore, useThemeSync } from '@/features/theme/theme-store'

export interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
})

function RootComponent() {
  const preference = useThemeStore((state) => state.preference)
  // The shelf brings its own shell and pins its own density.
  const shelf = useRouterState({
    select: (state) => state.location.pathname.startsWith('/shelf'),
  })
  useDensityAttribute(shelf ? 'shelf' : undefined)
  useThemeSync()

  return (
    <TooltipProvider delayDuration={300}>
      <AuthGate>
        {shelf ? (
          <Outlet />
        ) : (
          <AppShell>
            <Outlet />
          </AppShell>
        )}
        <GlobalScanListener />
        <CommandPalette />
      </AuthGate>
      <Toaster
        position={shelf ? 'bottom-center' : 'bottom-right'}
        offset={shelf ? 84 : undefined}
        theme={preference === 'system' ? 'system' : preference}
        toastOptions={{
          // Square and neutral like everything else; only a failure takes colour.
          classNames: {
            toast: 'rounded-none! border-divider! bg-ground! text-ink! font-sans! shadow-md!',
            error: 'text-destructive!',
          },
        }}
      />
    </TooltipProvider>
  )
}

function NotFound() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-start gap-4 p-10">
      <h1 className="text-2xl font-semibold tracking-tight">Nothing here</h1>
      <p className="text-muted-foreground text-sm">
        That address does not match anything in this inventory.
      </p>
      <Button asChild variant="outline">
        <Link to="/">Back to the overview</Link>
      </Button>
    </div>
  )
}
